import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Mic, Square, Play, Pause, Wand2, Download, RotateCcw, Sparkles, Loader2, Upload, Image as ImageIcon, Send, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';
import Navigation from '../components/Navigation';
import { ReportAIContentButton } from '../components/ReportAIContent';

/**
 * Voice-to-Avatar — Studio MVP
 *
 * Three creation paths:
 *  1) Pick a stylized SVG skin (anime / pixar / destinemoji / realistic) and record a voice ➝ lip-sync the SVG mouth via Web Audio RMS
 *  2) Upload a real photo + pick a style ➝ Gemini Nano Banana generates an AI portrait that resembles you
 *  3) Publish the avatar (image + recorded voice) as a 24h Story
 */

const STYLES = [
  { id: 'anime',       label: 'Anime japonais', accent: '#ec4899', skin: '#ffd9c4', hair: '#1f2937', eye: '#3b82f6' },
  { id: 'pixar',       label: 'Cartoon Pixar',  accent: '#f59e0b', skin: '#f9c89b', hair: '#7c2d12', eye: '#16a34a' },
  { id: 'destinemoji', label: 'Destinemoji',    accent: '#06b6d4', skin: '#fcd5ad', hair: '#000000', eye: '#1f2937' },
  { id: 'realiste',    label: 'Réaliste',       accent: '#a855f7', skin: '#e8b48f', hair: '#3b2618', eye: '#6b4423' },
];

const SKIN_TONES   = ['#f6d7b2', '#e8b48f', '#c98e63', '#a06840', '#6b4423', '#3b2618'];
const HAIR_COLORS  = ['#1f2937', '#3b2618', '#7c2d12', '#b45309', '#fbbf24', '#9ca3af', '#ec4899', '#ffffff'];
const EYE_COLORS   = ['#3b82f6', '#16a34a', '#a855f7', '#6b4423', '#1f2937', '#0891b2'];

const pickMimeType = () => {
  const cs = ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  for (const c of cs) {
    try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c; } catch (_) {}
  }
  return '';
};
const extForMime = (m) => {
  if (!m) return 'webm';
  if (m.includes('mp4') || m.includes('aac')) return 'mp4';
  if (m.includes('ogg')) return 'ogg';
  return 'webm';
};

const Studio = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const STYLES_TR = [
    { ...STYLES[0], label: t('studio.styleAnime') || STYLES[0].label },
    { ...STYLES[1], label: t('studio.stylePixar') || STYLES[1].label },
    { ...STYLES[2], label: t('studio.styleDestinemoji') || STYLES[2].label },
    { ...STYLES[3], label: t('studio.styleRealiste') || STYLES[3].label },
  ];
  const [styleObj, setStyleObj] = useState(STYLES_TR[0]);
  const [skinTone, setSkinTone]  = useState(STYLES[0].skin);
  const [hairColor, setHairColor] = useState(STYLES[0].hair);
  const [eyeColor, setEyeColor]   = useState(STYLES[0].eye);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedExt, setRecordedExt]   = useState('webm');
  const [processedBlob, setProcessedBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(0);
  const [effects, setEffects] = useState([]);
  const [applyingEffect, setApplyingEffect] = useState(false);
  // AI photo-to-avatar
  const [aiImage, setAiImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const audioRef = useRef(null);
  const mouthIntervalRef = useRef(null);
  const durationTimerRef = useRef(null);
  const photoInputRef = useRef(null);

  // Sync default colors when changing style preset
  useEffect(() => {
    setSkinTone(styleObj.skin);
    setHairColor(styleObj.hair);
    setEyeColor(styleObj.eye);
  }, [styleObj.id]);

  // Load fun effects
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/audio-effects/presets');
        setEffects(res.data?.fun || []);
      } catch (_) {}
    })();
  }, []);

  // ---- Recording ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      const mimeType = pickMimeType();
      const ext = extForMime(mimeType);
      setRecordedExt(ext);
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordedChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data?.size) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const actual = mimeType || 'audio/webm';
        const blob = new Blob(recordedChunksRef.current, { type: actual });
        setRecordedBlob(new File([blob], `voice.${ext}`, { type: actual }));
        setProcessedBlob(null);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      setRecording(true);
      setDuration(0);
      durationTimerRef.current = setInterval(() => {
        setDuration(d => {
          if (d >= 60) { stopRecording(); return 60; }
          return d + 1;
        });
      }, 1000);
    } catch (_) {
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch (_) {}
    if (durationTimerRef.current) { clearInterval(durationTimerRef.current); durationTimerRef.current = null; }
    setRecording(false);
  };

  const resetAll = () => {
    setRecordedBlob(null); setProcessedBlob(null); setDuration(0); setMouthOpen(0);
  };

  const applyEffect = async (presetId) => {
    if (!recordedBlob) return;
    setApplyingEffect(true);
    try {
      const fd = new FormData();
      // CRITICAL: include a filename with the proper extension (mp4/m4a on iOS,
      // webm on Android/Chrome). Without it the backend defaults to "webm"
      // and ffmpeg fails to decode an iOS mp4 → "Impossible d'appliquer l'effet".
      const fname = recordedBlob.name || `voice.${recordedExt || 'webm'}`;
      fd.append('audio_file', recordedBlob, fname);
      fd.append('preset_id', presetId);
      fd.append('engine', 'ffmpeg');
      const res = await axios.post('/api/audio-effects/preview', fd, { responseType: 'blob' });
      // Wrap response into a proper Blob with mp3 type (backend returns audio/mpeg)
      const processed = new Blob([res.data], { type: 'audio/mpeg' });
      processed.name = `voice_${presetId}.mp3`;
      setProcessedBlob(processed);
      toast.success('Effet appliqué');
    } catch (err) {
      console.error('apply effect failed', err);
      toast.error("Impossible d'appliquer l'effet");
    }
    finally { setApplyingEffect(false); }
  };

  const currentBlob = processedBlob || recordedBlob;
  // CRITICAL: do NOT call URL.createObjectURL on every render — it creates a
  // new blob URL each time, which makes the <audio> element constantly reload
  // (visible as "--:--" duration / infinite buffering) and breaks playback +
  // voice-effect application. Memoize and revoke the previous URL on change.
  const currentUrl = useMemo(() => {
    if (!currentBlob) return null;
    return URL.createObjectURL(currentBlob);
  }, [currentBlob]);

  useEffect(() => {
    // Revoke the previous blob URL whenever it changes (or on unmount).
    return () => { if (currentUrl) { try { URL.revokeObjectURL(currentUrl); } catch (_) {} } };
  }, [currentUrl]);

  // ---- Lip-sync (procedural oscillation — robust, no Web Audio API) ----
  const startLipSync = () => {
    if (mouthIntervalRef.current) return;
    let t = 0;
    mouthIntervalRef.current = setInterval(() => {
      t += 0.18;
      // Natural-looking talking: a sine wave + small random jitter,
      // clamped to [0, 1]. Faster oscillation than a heartbeat to feel like speech.
      const base = (Math.sin(t * 4) + 1) / 2; // 0..1
      const jitter = (Math.random() - 0.5) * 0.25;
      const next = Math.max(0, Math.min(1, base * 0.65 + jitter + 0.15));
      setMouthOpen(next);
    }, 80);
  };
  const stopLipSync = () => {
    if (mouthIntervalRef.current) {
      clearInterval(mouthIntervalRef.current);
      mouthIntervalRef.current = null;
    }
    setMouthOpen(0);
  };
  const onPlay  = () => { setPlaying(true);  startLipSync(); };
  const onPause = () => { setPlaying(false); stopLipSync(); };
  const onEnded = () => { setPlaying(false); stopLipSync(); };

  // ---- Download ----
  const downloadAudio = () => {
    if (!currentBlob) return;
    const url = URL.createObjectURL(currentBlob);
    const a = document.createElement('a');
    a.href = url; a.download = `destine-avatar-${Date.now()}.${recordedExt}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ---- AI photo to avatar (Nano Banana) ----
  const generateFromPhoto = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo trop volumineuse (max 10 MB)'); return; }
    setGenerating(true);
    setAiImage(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('style', styleObj.id);
      const res = await axios.post('/api/studio/avatar/from-photo', fd, { timeout: 60000 });
      setAiImage(res.data.image_url);
      toast.success(`Avatar généré ! ${res.data.remaining} restant(s) aujourd'hui`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Génération impossible');
    } finally { setGenerating(false); }
  };

  // ---- Publish as Story ----
  // Snapshot the SVG avatar to a PNG blob (for publishing as a Story).
  // Returns null if no SVG is rendered (e.g. when aiImage is used instead).
  const snapshotAvatarSVG = () => new Promise((resolve) => {
    try {
      const svgEl = document.querySelector('[data-testid="avatar-svg"]');
      if (!svgEl) return resolve(null);
      const clone = svgEl.cloneNode(true);
      // Ensure standalone width/height attributes for rasterizing
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const w = 480, h = 520; // 2x the visual size for crisp output
      clone.setAttribute('width', String(w));
      clone.setAttribute('height', String(h));
      const xml = new XMLSerializer().serializeToString(clone);
      const svg64 = btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const ctx = c.getContext('2d');
          // Solid dark background so the avatar pops in Story viewer
          const g = ctx.createLinearGradient(0, 0, 0, h);
          g.addColorStop(0, '#1a0033');
          g.addColorStop(1, '#3b0a4f');
          ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          c.toBlob(b => resolve(b), 'image/png', 0.92);
        } catch (_) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = 'data:image/svg+xml;base64,' + svg64;
    } catch (_) { resolve(null); }
  });

  const publishAvatarAsStory = async () => {
    if (!aiImage && !currentBlob) {
      toast.error('Enregistrez votre voix ou générez un avatar IA d\'abord');
      return;
    }
    setPublishing(true);
    try {
      const fd = new FormData();
      if (aiImage) {
        fd.append('image_url', aiImage);
      } else {
        // No AI image — rasterize the current SVG avatar so the story actually
        // displays the user's chosen skin/colors/style on top of the audio.
        const snap = await snapshotAvatarSVG();
        if (snap) fd.append('image_file', snap, `avatar_${styleObj.id}.png`);
      }
      fd.append('style', styleObj.id);
      if (currentBlob) {
        fd.append('audio', currentBlob, currentBlob.name || `voice.${recordedExt}`);
      }
      const res = await axios.post('/api/studio/avatar/publish-story', fd);
      if (res.data?.id) toast.success('Avatar publié en Story (24h)');
      else toast.error('Réponse inattendue');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publication impossible');
    } finally { setPublishing(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-black to-fuchsia-900 text-white pb-24" data-testid="studio-page">
      <div className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <button onClick={() => navigate('/feed')} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="studio-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2 flex-wrap">
              <Sparkles className="w-5 h-5 text-fuchsia-400 shrink-0" />
              <span>{t('studio.voiceToAvatar')}</span>
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">{t('studio.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* Avatar canvas */}
        <div className="rounded-3xl bg-black/40 border border-white/10 p-6 flex flex-col items-center" data-testid="avatar-canvas">
          <div className={`relative ${playing ? 'studio-avatar-talking' : ''}`} data-testid="avatar-anim-wrapper">
          {aiImage ? (
            <div
              className="relative w-64 h-64 rounded-2xl overflow-hidden shadow-xl"
              style={{
                transform: `scale(${1 + mouthOpen * 0.08})`,
                boxShadow: `0 0 ${20 + mouthOpen * 60}px ${mouthOpen * 12}px rgba(217, 70, 239, ${0.3 + mouthOpen * 0.6})`,
                transition: 'transform 70ms linear, box-shadow 70ms linear',
              }}
            >
              <img src={getMediaUrl(aiImage)} alt="Avatar IA" className="w-full h-full object-cover" data-testid="ai-avatar-image" />
              {/* Mouth overlay — soft ellipse near the lower-center */}
              {playing && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/55 border border-black/40 pointer-events-none"
                  style={{
                    bottom: '22%',
                    width: `${28 + mouthOpen * 18}px`,
                    height: `${6 + mouthOpen * 32}px`,
                    transition: 'width 70ms linear, height 70ms linear',
                    boxShadow: 'inset 0 -2px 4px rgba(255,255,255,0.15)',
                  }}
                  data-testid="ai-avatar-mouth"
                />
              )}
              {/* Equalizer bars when playing */}
              {playing && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-end gap-1 h-6">
                  {[0, 1, 2, 3, 4].map(i => (
                    <span
                      key={i}
                      className="w-1 bg-fuchsia-400 rounded-full"
                      style={{
                        height: `${Math.max(3, mouthOpen * 22 * (0.6 + ((i + 1) % 3) * 0.25))}px`,
                        transition: 'height 70ms linear',
                        opacity: 0.85,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <AvatarSVG style={styleObj} skin={skinTone} hair={hairColor} eye={eyeColor} mouthOpen={mouthOpen} playing={playing} />
          )}
          </div>
          {aiImage && (
            <div className="mt-3 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={async () => {
                  try {
                    const url = getMediaUrl(aiImage);
                    const res = await fetch(url);
                    const blob = await res.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = `avatar_destinestudio_${Date.now()}.png`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
                    toast.success('Avatar téléchargé');
                  } catch (_) { toast.error('Téléchargement impossible'); }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-semibold shadow-lg"
                data-testid="ai-download-btn"
              >
                <Download className="w-4 h-4" />
                Télécharger
              </button>
              <ReportAIContentButton feature="ai_avatar" mediaUrl={aiImage} testId="ai-report-avatar" />
              <button onClick={() => setAiImage(null)} className="text-xs text-fuchsia-300 hover:text-white underline" data-testid="ai-clear-btn">
                {t('studio.backTo2D')}
              </button>
            </div>
          )}
        </div>

        {/* Style picker */}
        <div>
          <p className="text-sm font-semibold text-gray-300 mb-3">{t('studio.pickStyle')}</p>
          <div className="grid grid-cols-2 gap-3">
            {STYLES_TR.map(s => (
              <button
                key={s.id}
                onClick={() => setStyleObj(s)}
                className={`p-3 rounded-2xl border transition-all ${styleObj.id === s.id ? 'border-fuchsia-500 bg-fuchsia-500/15' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                data-testid={`style-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ background: s.skin, border: `2px solid ${s.accent}` }} />
                  <span className="text-sm font-medium text-left">{s.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Color customization (only for SVG skin mode) */}
        {!aiImage && (
          <div className="space-y-3 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="color-customization">
            <p className="text-sm font-semibold text-gray-300">{t('studio.customizeColors')}</p>
            <ColorRow label={t('studio.skin')}    options={SKIN_TONES}  value={skinTone}  onChange={setSkinTone}  testid="skin-color" />
            <ColorRow label={t('studio.hair')} options={HAIR_COLORS} value={hairColor} onChange={setHairColor} testid="hair-color" />
            <ColorRow label={t('studio.eyes')}    options={EYE_COLORS}  value={eyeColor}  onChange={setEyeColor}  testid="eye-color" />
          </div>
        )}

        {/* Photo → AI Avatar */}
        <div className="rounded-2xl bg-gradient-to-br from-violet-700/30 to-fuchsia-700/20 border border-fuchsia-500/30 p-5" data-testid="ai-avatar-section">
          <p className="text-sm font-semibold text-fuchsia-200 flex items-center gap-2 mb-2">
            <Wand2 className="w-4 h-4" /> {t('studio.aiAvatar')}
          </p>
          <p className="text-xs text-gray-300 mb-3">
            {t('studio.aiAvatarHint')}
          </p>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={e => generateFromPhoto(e.target.files?.[0])} data-testid="ai-photo-input" />
          <button
            onClick={() => photoInputRef.current?.click()}
            disabled={generating}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
            data-testid="ai-photo-pick"
          >
            {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
            {generating ? t('studio.generating') : (aiImage ? t('studio.regenerate') : t('studio.uploadPhoto'))}
          </button>
        </div>

        {/* Recorder */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-300">{t('studio.recordSection')}</span>
            {duration > 0 && (
              <span className="text-xs text-gray-400 font-mono" data-testid="record-duration">
                {String(Math.floor(duration / 60)).padStart(2, '0')}:{String(duration % 60).padStart(2, '0')}
              </span>
            )}
          </div>
          {!recording && !recordedBlob && (
            <button onClick={startRecording} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 active:bg-fuchsia-800 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="record-start">
              <Mic className="w-5 h-5" /> {t('studio.start')}
            </button>
          )}
          {recording && (
            <button onClick={stopRecording} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 animate-pulse" data-testid="record-stop">
              <Square className="w-5 h-5" /> {t('studio.stopRecord')}
            </button>
          )}
          {recordedBlob && !recording && (
            <div className="flex gap-2">
              <button onClick={resetAll} className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="record-reset">
                <RotateCcw className="w-4 h-4" /> {t('studio.resume')}
              </button>
              <button onClick={downloadAudio} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="record-download">
                <Download className="w-4 h-4" /> {t('studio.download')}
              </button>
            </div>
          )}
        </div>

        {/* Effects */}
        {recordedBlob && effects.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2"><Wand2 className="w-4 h-4" /> {t('studio.voiceEffects')}</p>
            <div className="grid grid-cols-3 gap-2">
              {effects.slice(0, 9).map(e => (
                <button key={e.id} onClick={() => applyEffect(e.id)} disabled={applyingEffect} className="px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm flex flex-col items-center gap-1 disabled:opacity-50" data-testid={`effect-${e.id}`}>
                  <span className="text-xl">{e.emoji || '🎙'}</span>
                  <span className="text-[11px] truncate w-full text-center">{e.label}</span>
                </button>
              ))}
            </div>
            {applyingEffect && <div className="flex items-center justify-center gap-2 text-xs text-fuchsia-300 mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('studio.applying')}</div>}
            {processedBlob && (
              <button onClick={() => setProcessedBlob(null)} className="mt-3 w-full text-xs text-gray-400 hover:text-white underline" data-testid="effect-clear">
                {t('studio.backToOriginal')}
              </button>
            )}
          </div>
        )}

        {/* Audio player + animate */}
        {currentUrl && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5 space-y-3" data-testid="player-section">
            {/* Native audio player — visible above the Animate button so user can
                 scrub/replay/download directly, exactly like before the refactor. */}
            <audio
              ref={audioRef}
              src={currentUrl}
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              controls
              preload="auto"
              className="w-full"
              data-testid="avatar-audio"
            />
            <button
              onClick={async () => {
                const a = audioRef.current;
                if (!a) return;
                if (playing) {
                  a.pause();
                  return;
                }
                try {
                  // If buffer not ready, wait for canplay then play
                  if (a.readyState < 2) {
                    await new Promise((resolve, reject) => {
                      const ok = () => { a.removeEventListener('canplay', ok); a.removeEventListener('error', ko); resolve(); };
                      const ko = () => { a.removeEventListener('canplay', ok); a.removeEventListener('error', ko); reject(new Error('audio error')); };
                      a.addEventListener('canplay', ok, { once: true });
                      a.addEventListener('error', ko, { once: true });
                      try { a.load(); } catch (_) {}
                      setTimeout(ko, 5000);
                    });
                  }
                  await a.play();
                } catch (err) {
                  console.error('Avatar play failed', err);
                  toast.error('Lecture impossible — réessayez');
                  setPlaying(false);
                  stopLipSync();
                }
              }}
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="animate-btn"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              {playing ? t('studio.playing') : t('studio.animate')}
            </button>
          </div>
        )}

        {/* Publish to Story — visible as soon as user has SOMETHING to share (voice OR AI image) */}
        {(aiImage || currentBlob) && (
          <button
            onClick={publishAvatarAsStory}
            disabled={publishing}
            className="w-full bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
            data-testid="publish-story-btn"
          >
            {publishing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
            {t('studio.publishStory')}
          </button>
        )}
      </div>

      <Navigation />
    </div>
  );
};


const ColorRow = ({ label, options, value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-400 w-16">{label}</span>
    <div className="flex flex-wrap gap-1.5">
      {options.map(c => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className={`w-7 h-7 rounded-full border-2 transition-transform ${value === c ? 'border-white scale-110' : 'border-white/20'}`}
          style={{ background: c }}
          data-testid={`${testid}-${c.replace('#', '')}`}
          aria-label={`${label} ${c}`}
        />
      ))}
    </div>
  </div>
);


const AvatarSVG = ({ style, skin, hair, eye, mouthOpen, playing }) => {
  // Wider amplitude when playing so the user clearly sees the mouth moving.
  const m = Math.max(0.06, Math.min(1, mouthOpen)) * (playing ? 36 : 20);
  const rxMouth = (playing ? 16 : 14) + mouthOpen * 8;
  const ryMouth = m / 2 + 2;
  return (
    <svg viewBox="0 0 220 240" width="240" height="260" className="drop-shadow-2xl" data-testid="avatar-svg">
      <defs>
        <radialGradient id="bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={style.accent} stopOpacity={playing ? 0.7 : 0.4} />
          <stop offset="100%" stopColor={style.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="110" cy="120" r="105" fill="url(#bg)" />
      <ellipse cx="110" cy="80" rx="78" ry="55" fill={hair} />
      <ellipse cx="110" cy="125" rx="62" ry="74" fill={skin} stroke="#0006" strokeWidth="1.5" />
      {style.id === 'anime' && (<path d="M48 100 Q60 50 110 55 Q160 50 172 100 Q150 80 120 78 Q90 80 75 92 Q60 105 48 100 Z" fill={hair} />)}
      {style.id === 'pixar' && (<path d="M55 102 Q75 60 110 60 Q155 65 168 105 Q150 92 130 90 Q110 91 90 95 Q72 100 55 102 Z" fill={hair} />)}
      {style.id === 'destinemoji' && (<path d="M50 95 Q70 55 110 55 Q150 55 170 95 Q140 82 110 82 Q80 82 50 95 Z" fill={hair} />)}
      {style.id === 'realiste' && (<path d="M55 98 Q70 62 110 62 Q150 62 168 100 Q145 90 115 90 Q88 92 55 98 Z" fill={hair} />)}
      <g>
        <ellipse cx="86" cy="125" rx={style.id === 'anime' ? 11 : 8} ry={style.id === 'anime' ? 13 : 9} fill="white" stroke="#0008" strokeWidth="1.2" />
        <ellipse cx="134" cy="125" rx={style.id === 'anime' ? 11 : 8} ry={style.id === 'anime' ? 13 : 9} fill="white" stroke="#0008" strokeWidth="1.2" />
        <circle cx="86" cy="128" r={style.id === 'anime' ? 6 : 4.5} fill={eye} />
        <circle cx="134" cy="128" r={style.id === 'anime' ? 6 : 4.5} fill={eye} />
        <circle cx="84" cy="125" r="1.6" fill="white" />
        <circle cx="132" cy="125" r="1.6" fill="white" />
      </g>
      <path d={`M72 ${style.id === 'anime' ? 102 : 108} Q86 ${style.id === 'anime' ? 95 : 102} 100 ${style.id === 'anime' ? 102 : 108}`} stroke={hair} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d={`M120 ${style.id === 'anime' ? 102 : 108} Q134 ${style.id === 'anime' ? 95 : 102} 148 ${style.id === 'anime' ? 102 : 108}`} stroke={hair} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M110 138 Q108 152 110 162 Q113 160 116 161" stroke="#0007" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Mouth — opens visibly with mouthOpen */}
      <ellipse cx="110" cy="180" rx={rxMouth} ry={ryMouth} fill="#7f1d1d" stroke="#0009" strokeWidth="1.5" style={{ transition: 'rx 70ms linear, ry 70ms linear' }} />
      {playing && ryMouth > 5 && <rect x={110 - rxMouth + 4} y={180 - 2} width={(rxMouth - 4) * 2} height="3" fill="white" rx="1" />}
      {(style.id === 'pixar' || style.id === 'destinemoji') && (
        <>
          <ellipse cx="75" cy="160" rx="9" ry="5" fill="#fb7185" opacity="0.4" />
          <ellipse cx="145" cy="160" rx="9" ry="5" fill="#fb7185" opacity="0.4" />
        </>
      )}
    </svg>
  );
};

export default Studio;
